
/// <amd-module name="Build/PipelinesLanguage/Utils/MonacoConverters" />

import {
    Command, CompletionItem, CompletionItemKind, CompletionList,
    Diagnostic, FormattingOptions, Hover, InsertTextFormat,
    Location, MarkedString,MarkupContent, MarkupKind, Position,
    Range, SymbolInformation, SymbolKind, TextDocument, TextEdit
} from "AzurePipelinesLanguageService"
import { pipelinesModelUri } from "../Constants";

/**
 * Create LSP TextDocument based on Monaco model.
 * @param model Monaco model that represents the editing document.
 */
export function createDocument(model: monaco.editor.IReadOnlyModel): TextDocument {
    return TextDocument.create(pipelinesModelUri, model.getModeId(), model.getVersionId(), model.getValue());
}

export interface ProtocolCompletionItem extends monaco.languages.CompletionItem {
    data?: any;
    fromEdit?: boolean;
    documentationFormat?: MarkupKind;
    originalItemKind?: CompletionItemKind;
    deprecated?: boolean;
}

function isProtocolCompletionItem(item: any): item is ProtocolCompletionItem {
    return !!item && 'data' in item;
}

/**
 * This namespace contains a set of conversions from Monaco to Language Server Protocol (LSP).
 * Language service functionality is based on vscode-languageserver-protocol that uses types 
 * defined in vscode-languageserver-types. Thus conversion between Monaco and vscode LSP is needed.
 */
export namespace MonacoToProtocolConverter {

    /**
     * Converts the Monaco one based position into zero based LSP position.
     */
    export function asPosition(lineNumber: number, column: number): Position {
        return {
            line: lineNumber - 1,
            character: column - 1
        };
    }

    /**
     * Converts Monaco CompletionItem into LSP CompletionItem.
     * @param item A completion item represents a text snippet that is proposed to complete text that is being typed.
     */
    export function asCompletionItem(item: monaco.languages.CompletionItem): CompletionItem {
        const lspCompletionItem: CompletionItem = { label: item.label as string };
        const protocolItem = isProtocolCompletionItem(item) ? item : undefined;
        
        if (item.detail) { lspCompletionItem.detail = item.detail; }

        // We only send items back we created. So this can't be something else than a string right now.
        if (item.documentation) {
            if (protocolItem && protocolItem.documentationFormat) {
                lspCompletionItem.documentation = asDocumentation(protocolItem.documentationFormat, item.documentation);
            } else {
                lspCompletionItem.documentation = item.documentation as string;
            }
        }

        fillPrimaryInsertText(lspCompletionItem, item as ProtocolCompletionItem);

        if ( typeof item.kind === "number") {
            lspCompletionItem.kind = asCompletionItemKind(item.kind, protocolItem && protocolItem.originalItemKind);
        }
        if (item.filterText) { lspCompletionItem.filterText = item.filterText; }
        if (item.sortText) { lspCompletionItem.sortText = item.sortText; }
        if (item.additionalTextEdits) { lspCompletionItem.additionalTextEdits = asTextEdits(item.additionalTextEdits); }
        if (item.command) { lspCompletionItem.command = asCommand(item.command); }
        if (item.commitCharacters) { lspCompletionItem.commitCharacters = item.commitCharacters.slice(); }
        if (protocolItem) {
            if (protocolItem.data !== undefined) {
                lspCompletionItem.data = protocolItem.data;
            }
        }
        return lspCompletionItem;
    }

    /**
     * Document formating options conversion from Monaco to LSP.
     */
    export function asFormattingOptions(options: monaco.languages.FormattingOptions): FormattingOptions {
        return {
            tabSize: options.tabSize,
            insertSpaces: options.insertSpaces,
        };
    }

    function asDocumentation(format: MarkupKind, documentation: string | monaco.IMarkdownString): string | MarkupContent {
        switch (format) {
            case MarkupKind.PlainText:
                return { kind: format, value: documentation as string };
            case MarkupKind.Markdown:
                return { kind: format, value: (documentation as monaco.IMarkdownString).value };
            default:
                return `Unsupported Markup content received. Kind is: ${format}`;
        }
    }

    function fillPrimaryInsertText(target: CompletionItem, source: ProtocolCompletionItem): void {
        let format: InsertTextFormat = InsertTextFormat.PlainText;
        let text: string | undefined;
        let range: Range | undefined;
        if (source.data) {
            text = source.data.text;
            range = asRange(source.data.range);
        } else if (source.insertText) {
            format = InsertTextFormat.Snippet;
            text = source.insertText;
        }
        if (source.range) {
            range = asRange(source.range as monaco.IRange);
        }

        target.insertTextFormat = format;
        if (source.fromEdit && text && range) {
            target.textEdit = { newText: text, range: range };
        } else {
            target.insertText = text;
        }
    }

    function asCompletionItemKind(value: monaco.languages.CompletionItemKind, original: CompletionItemKind | undefined): CompletionItemKind {
        if (original !== undefined) {
            return original;
        }

        if (typeof monaco === "undefined") {
            return CompletionItemKind.Text;
        }

        const monacoKindName = monaco.languages.CompletionItemKind[value];
        const providerKindNamespace = CompletionItemKind as {[key: string]: number};
        return providerKindNamespace[monacoKindName] as CompletionItemKind;
    }

    function asTextEdit(edit: monaco.editor.ISingleEditOperation): TextEdit {
        const range = asRange(edit.range)!;
        return {
            range,
            newText: edit.text!
        }
    }

    function asTextEdits(items: monaco.editor.ISingleEditOperation[]): TextEdit[] | undefined {
        if (!items) {
            return undefined;
        }
        return items.map(item => asTextEdit(item));
    }

    
    function asRange(range: monaco.IRange): Range | undefined {
        if (range === undefined || range === null) {
            return undefined;
        }

        const start = asPosition(range.startLineNumber, range.startColumn);
        const end = asPosition(range.endLineNumber, range.endColumn);

        if (start === undefined || end === undefined) {
            return undefined;
        }

        return {
            start, end
        };
    }

    function asCommand(item: monaco.languages.Command): Command | undefined {
        if (item) {
            const args = item.arguments || [];
            return Command.create(item.title, item.id, ...args);
        }
        return undefined;
    }
}

/**
 * This namespace contains a set of conversions from Language Server Protocol (LSP) to Monaco.
 * Language service functionality is based on vscode-languageserver-protocol that uses types
 * defined in vscode-languageserver-types. Thus conversion between Monaco and vscode LSP is needed.
 */
export namespace ProtocolToMonacoConverter {
    /**
     * Convert LSP completion items into Monaco CompletionList
     * Note: Monaco changed the CompletionList contract by renaming `items` to `suggestions`. Hence the intersection
     * type added in the return type declaration of this function.
     */
    export function asCompletionList(completionItems: CompletionItem[] | CompletionList): monaco.languages.CompletionList & { suggestions: monaco.languages.CompletionItem[] } {
        if (!completionItems) {
            return {
                incomplete: false,
                suggestions: [],
            };
        }

        if (Array.isArray(completionItems)) {
            const items = completionItems.map(item => asCompletionItem(item));
            return {
                incomplete: false,
                suggestions: items,
            }
        }

        const items = completionItems.items.map(asCompletionItem);
        return {
            incomplete: completionItems.isIncomplete,
            suggestions: items,
        };
    }

    /**
     * Convert single completion item into extended monaco CompletionItem.
     */
    export function asCompletionItem(item: CompletionItem): ProtocolCompletionItem {
        const protocolCompletionItem: ProtocolCompletionItem = {label: item.label } as ProtocolCompletionItem;

        if (item.detail) {
            protocolCompletionItem.detail = item.detail;
        }
        if (item.documentation) {
            protocolCompletionItem.documentation = asDocumentation(item.documentation);
            protocolCompletionItem.documentationFormat = typeof item.documentation === "string" ? undefined : item.documentation.kind;
        };
        if (item.filterText) {
            protocolCompletionItem.filterText = item.filterText;
        }
        const insertText = asCompletionInsertText(item);
        if (insertText) {
            protocolCompletionItem.insertText = insertText.text;
            protocolCompletionItem.range = insertText.range!;
            protocolCompletionItem.fromEdit = insertText.fromEdit;
            protocolCompletionItem.insertTextRules = insertText.insertTextRules;
        }
        if (typeof item.kind === "number") {
            const [itemKind, original] = asCompletionItemKind(item.kind);
            protocolCompletionItem.kind = itemKind;
            if (original) {
                protocolCompletionItem.originalItemKind = original;
            }
        }
        if (item.sortText) {
            protocolCompletionItem.sortText = item.sortText;
        }
        if (item.additionalTextEdits) {
            protocolCompletionItem.additionalTextEdits = asEditOperations(item.additionalTextEdits);
        }
        if (item.commitCharacters instanceof Array) {
            protocolCompletionItem.commitCharacters = item.commitCharacters.slice();
        }
        if (item.command) {
            protocolCompletionItem.command = asCommand(item.command);
        }
        if (item.data) {
            protocolCompletionItem.data = item.data;
        }
        return protocolCompletionItem;
    }

    /**
     * Convert text edit operation from LSP to Monaco ISingleEditOperation
     */
    export function asEditOperations(items: TextEdit[]): monaco.editor.ISingleEditOperation[] | undefined {
        if (!items) {
            return undefined;
        }
        return items.map(item => asEditOperation(item)).filter(item => !!item) as monaco.editor.ISingleEditOperation[];
    }

    function asEditOperation(edit: TextEdit): monaco.editor.ISingleEditOperation | undefined {
        if (!edit) {
            return undefined;
        }
        const range = asRange(edit.range)!;
        return {
            range,
            text: edit.newText
        }
    }

    /**
     * Convert text edit operations from LSP to Monaco.
     */
    export function asTextEdits(items: TextEdit[]): monaco.languages.TextEdit[] | undefined {
        if (!items) {
            return undefined;
        }
        return items.map(item => asTextEdit(item)).filter(item => !!item) as monaco.languages.TextEdit[];
    }

    function asTextEdit(edit: TextEdit): monaco.languages.TextEdit | undefined {
        if (!edit) {
            return undefined;
        }
        const range = asRange(edit.range)!;
        return {
            range,
            text: edit.newText
        }
    }
    
    function asCompletionInsertText(item: CompletionItem): { text: string, insertTextRules?: monaco.languages.CompletionItemInsertTextRule, range?: monaco.Range, fromEdit: boolean } | undefined {
        if (item.textEdit) {
            return {
                text: item.textEdit.newText,
                range: asRange((item.textEdit as TextEdit).range)!,
                fromEdit: true,
                insertTextRules: item.insertTextFormat === InsertTextFormat.Snippet ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined
            };
        }
        if (item.insertText) {
            const value = item.insertText;
            const text = item.insertTextFormat === InsertTextFormat.Snippet ? { value } : value;
            return {
                text: item.insertText,
                fromEdit: false,
                insertTextRules: item.insertTextFormat === InsertTextFormat.Snippet ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined
            };
        }
        return undefined;
    }

    function asCompletionItemKind(value: CompletionItemKind): [monaco.languages.CompletionItemKind, CompletionItemKind | undefined] {
        // monaco and the language provider use different enums for type that are not guaranteed to be in sync
        // So, we treat the enums as name -> number maps and match things up by name rather than by number
        const providerKindNamespace = CompletionItemKind as {[key: string]: number};
        const providerKindName = Object.keys(providerKindNamespace).find(k => providerKindNamespace[k] === value);
        if (providerKindName && typeof monaco !== "undefined") {
            return [monaco.languages.CompletionItemKind[providerKindName as keyof typeof monaco.languages.CompletionItemKind], value];
        }

        return [CompletionItemKind.Text, value];
    }

    function asDocumentation(value: string | MarkupContent): string | monaco.IMarkdownString {
        if (typeof value === "string") {
            return value;
        }
        if (value.kind === MarkupKind.PlainText) {
            return value.value;
        }
        return asMarkdownString(value);
    }

    function asRange(range: Range | undefined): monaco.Range | undefined {
        if (!range) {
            return undefined;
        }

        const start = asPosition(range.start);
        const end = asPosition(range.end);

        if (start === undefined || end === undefined) {
            return undefined;
        }

        return new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column);
    }

    function asMarkdownString(content: MarkedString | MarkupContent): monaco.IMarkdownString {
        if (isMarkedContent(content)) {
            return { value: content.value };
        }
        if (typeof content === "string") {
            return { value: content };
        }

        const { language, value } = content;
        
        return {
            value: '```' + language + '\n' + value + '\n```'
        };
    }

    function isMarkedContent(content: any): content is MarkupContent {
        return content && content.kind !== undefined;
    }

    export function asPosition(position: Position): monaco.Position | undefined {
        if (!position) {
            return undefined;
        }

        const { line, character } = position;
        if (line === undefined || character === undefined) {
            return undefined;
        }
        
        return new monaco.Position(line + 1, character + 1);
    }

    function asCommand(command: Command): monaco.languages.Command | undefined {
        if (!command) {
            return undefined;
        }
        return {
            id: command.command,
            title: command.title,
            arguments: command.arguments
        };
    }

    /**
     * Convert SymbolInformation from LSP to Monaco.
     * SymbolInformation represents information about programming constructs like variables, classes, interfaces etc.
     * @param values array of symbol information objects.
     * @param uri location of the editing document, optional.
     */
    export function asSymbolInformations(values: SymbolInformation[], uri?: monaco.Uri): monaco.languages.DocumentSymbol[] | undefined {
        if (!values) {
            return undefined;
        }
        return values.map(information => asSymbolInformation(information, uri)).filter(symbol => !!symbol) as monaco.languages.DocumentSymbol[];
    }

    function asSymbolInformation(item: SymbolInformation, uri?: monaco.Uri): monaco.languages.DocumentSymbol | undefined {
        const location = asLocation(uri ? { ...item.location, uri: uri.toString() } : item.location);
        if (!location) {
            return undefined;
        }

        return {
            name: item.name,
            containerName: item.containerName,
            kind: asSymbolKind(item.kind),
            range: location.range,
            selectionRange: location.range,
            detail: item.name,
            tags: []
        };
    }

    function asSymbolKind(item: SymbolKind): monaco.languages.SymbolKind {
        if (item <= SymbolKind.TypeParameter) {
            // Symbol kind is one based in the protocol and zero based in code.
            return item - 1;
        }
        return monaco.languages.SymbolKind.Property;
    }

    function asLocation(item: Location): monaco.languages.Location | undefined {
        if (!item) {
            return undefined;
        }
        const uri = monaco.Uri.parse(item.uri);
        const range = asRange(item.range)!;
        return {
            uri, range
        }
    }

    /**
     * Convert the result of hover request from LSP representation to Monaco.
     */
    export function asHover(hover: Hover): monaco.languages.Hover | undefined {
        if (!hover) {
            return undefined;
        }
        return {
            contents: asHoverContent(hover.contents),
            range: asRange(hover.range)
        };
    }

    function asHoverContent(contents: MarkedString | MarkedString[] | MarkupContent): monaco.IMarkdownString[] {
        if (Array.isArray(contents)) {
            return contents.map(content => asMarkdownString(content));
        }
        return [asMarkdownString(contents)];
    }

    /**
     * Convert diagnostics from LSP representation to Monaco.
     * @param diagnostics diagnostics array, such as a compiler errors or warnings.
     */
    export function asDiagnostics(diagnostics: Diagnostic[]): monaco.editor.IMarkerData[] | undefined {
        if (!diagnostics) {
            return undefined;
        }
        return diagnostics.map(diagnostic => asDiagnostic(diagnostic));
    }

    function asDiagnostic(diagnostic: Diagnostic): monaco.editor.IMarkerData {
        return {
            code: typeof diagnostic.code === "number" ? diagnostic.code.toString() : diagnostic.code,
            severity: asSeverity(diagnostic.severity),
            message: diagnostic.message,
            source: diagnostic.source,
            startLineNumber: diagnostic.range.start.line + 1,
            startColumn: diagnostic.range.start.character + 1,
            endLineNumber: diagnostic.range.end.line + 1,
            endColumn: diagnostic.range.end.character + 1,
            // relatedInformation: asRelatedInformations(diagnostic.relatedInformation)
        }
    }

    function asSeverity(severity?: number): monaco.MarkerSeverity {
        if (severity === 1) {
            return monaco.MarkerSeverity.Error;
        }
        if (severity === 2) {
            return monaco.MarkerSeverity.Warning;
        }
        if (severity === 3) {
            return monaco.MarkerSeverity.Info;
        }
        return monaco.MarkerSeverity.Hint;
    }
}
